import React, { useCallback } from "react"
import PropTypes from "prop-types"
import Analytics from "lib/Analytics"
import FormFieldset from "components/FormFieldset"
import i18n from "lib/i18n"
import paymentMethodPropType from "components/PropTypes/paymentMethodPropType"

const PaymentMethodChooser = ({
  paymentMethod = "credit_card",
  active = true,
  creditCardAvailable = false,
  directDebitAvailable = false,
  payPalAvailable = false,
  onChange = () => {},
}) => {
  const PAYMENT_METHODS = {
    creditCard: {
      label: "donation.label.credit_card",
      value: "credit_card",
      available: creditCardAvailable,
    },
    directDebit: {
      label: "donation.label.direct_debit",
      value: "direct_debit",
      available: directDebitAvailable,
    },
    paypal: {
      label: "PayPal",
      value: "paypal",
      available: payPalAvailable,
    },
  }

  const handleChange = useCallback(
    (e) => {
      const paymentMethod = e.target.value
      Analytics.trackFormEvent("Donation Form", "Completed", "Payment Method", paymentMethod)
      onChange({ paymentMethod })
    },
    [onChange],
  )

  // If there is only one payment method for that region, no need to show the selector
  const numProviders = [creditCardAvailable, directDebitAvailable, payPalAvailable].filter(
    (p) => p,
  ).length

  if (numProviders < 2) {
    return null
  }

  return (
    <div id="payment_method">
      <FormFieldset>
        <legend className="mb-3 text-xl font-semibold">
          {i18n.t("donation.heading.payment_method")}
        </legend>

        {Object.values(PAYMENT_METHODS)
          .filter((method) => method.available)
          .map((method) => {
            const inputId = `payment-method-${method.value}`
            return (
              <label
                htmlFor={inputId}
                key={method.value}
                className="flex min-h-11 w-full cursor-pointer items-center rounded-sm hover:bg-gray-50"
              >
                <input
                  id={inputId}
                  className="m-0 box-content size-7 shrink-0 cursor-pointer accent-blue-600"
                  onChange={handleChange}
                  value={method.value}
                  type="radio"
                  name="payment_method"
                  aria-label={i18n.t(method.label)}
                  checked={paymentMethod === method.value}
                  disabled={!active}
                />

                <span className="pl-3 text-sm" aria-hidden="true">
                  {i18n.t(method.label)}
                </span>
              </label>
            )
          })}
      </FormFieldset>
    </div>
  )
}

PaymentMethodChooser.propTypes = {
  active: PropTypes.bool,
  onChange: PropTypes.func,
  creditCardAvailable: PropTypes.bool,
  directDebitAvailable: PropTypes.bool,
  payPalAvailable: PropTypes.bool,
  paymentMethod: paymentMethodPropType,
}

export default React.memo(PaymentMethodChooser)
